import { useAtomValue, useSetAtom } from 'jotai';
import { useRef } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { Button, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useGeneratePlate } from '../../../api/progress/useGeneratePlate';
import { useGetGeneratePlate } from '../../../api/progress/useGetGeneratePlate';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { fileToBase64 } from './utils/fileToBase64';

interface GeneratePlateDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	registrationReceipt: File | null;
}

/**
 * GeneratePlateDetail component for generating plate.
 * Displays a file upload input for registration receipt and a submit button.
 */
export const GeneratePlateDetail = ({ actionItem }: GeneratePlateDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const generatePlateMutation = useGeneratePlate(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetGeneratePlate(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		watch,
	} = useForm<FormData>({
		defaultValues: {
			registrationReceipt: null,
		},
	});

	const selectedFile = watch('registrationReceipt');

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.registrationReceipt) {
			return;
		}

		try {
			const base64Data = await fileToBase64(data.registrationReceipt);

			await generatePlateMutation.mutateAsync({
				registrationReceipt: {
					base64Data,
					type: data.registrationReceipt.type,
					name: data.registrationReceipt.name,
				},
			});

			toast({
				message: 'Plate generated successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form
			reset();
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to generate plate';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Upload the registration receipt to complete online registration and generate a plate for the vehicle.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.plateGenerated ? (
					<Stack itemsSpacing='4'>
						<Text size='body' color='success'>
							✓ Plate generated
						</Text>
						{existingData.plateGeneratedDate && (
							<Text size='bodySmall' color='secondary'>
								Generated on{' '}
								{formatTimestampToDate(new Date(existingData.plateGeneratedDate).getTime()) ?? '—'}
							</Text>
						)}
					</Stack>
				) : null}

				{/* Show file input (allow re-upload even if complete) */}
				<Controller
					name='registrationReceipt'
					control={control}
					rules={{
						required: 'Please select a registration receipt file',
					}}
					render={({ field: { onChange } }) => (
						<Stack itemsSpacing='4'>
							<Text size='footnote'>Registration Receipt</Text>
							<input
								ref={fileInputRef}
								type='file'
								accept='.pdf,.png,.jpg,.jpeg'
								onChange={(event) => {
									const file = event.target.files?.[0];
									if (file) {
										onChange(file);
									}
								}}
								disabled={isSubmitting || generatePlateMutation.isPending}
								style={{
									padding: '8px',
									border: errors.registrationReceipt
										? '1px solid red'
										: '1px solid #ccc',
									borderRadius: '4px',
									width: '100%',
								}}
							/>
							{errors.registrationReceipt && (
								<Text size='footnote' color='error'>
									{errors.registrationReceipt.message}
								</Text>
							)}
							{selectedFile && (
								<Text size='footnote' color='secondary'>
									Selected: {selectedFile.name}
								</Text>
							)}
						</Stack>
					)}
				/>

				<Button
					size='large'
					isFullWidth
					onPress={() => {
						submitHandler();
					}}
					isLoading={isSubmitting || generatePlateMutation.isPending}
					isDisabled={!selectedFile || isLoadingData}>
					{actionItem.config.buttonTitle || 'Generate Plate'}
				</Button>
			</Stack>
		</Stack>
	);
};
